import type { ConversationMessage } from '@/api';

/**
 * How a long conversation is drawn (plan/16 §8, Gmail's shape): the oldest
 * message, a divider standing in for the middle, and the newest few. A stack
 * of a hundred one-line rows that each say "Discord" and a date is not a
 * reading pane.
 */

/** A conversation this long or shorter draws every row: no divider. */
export const SHORT_STACK = 5;
/** The newest messages always drawn under the divider. */
export const TAIL = 3;
/** How many hidden messages one click on a divider reveals. */
export const REVEAL_BATCH = 20;

export type StackEntry =
  | { kind: 'message'; message: ConversationMessage }
  /** A run of hidden messages, oldest first, drawn as one divider. */
  | { kind: 'hidden'; messages: ConversationMessage[] };

/**
 * The stack as drawn. `shown` is every message that must be on screen besides
 * the oldest and the newest `TAIL`: the unread and opened ones, the expanded
 * ones, and those a divider already revealed. Each run of the rest between two
 * drawn messages becomes one divider — so an unread message in the middle
 * splits the divider around itself rather than hiding behind it.
 */
export function stackEntries(
  messages: readonly ConversationMessage[],
  shown: ReadonlySet<string>,
): StackEntry[] {
  if (messages.length <= SHORT_STACK) {
    return messages.map((message) => ({ kind: 'message', message }));
  }
  const tailFrom = messages.length - TAIL;
  const out: StackEntry[] = [];
  let run: ConversationMessage[] = [];
  messages.forEach((message, index) => {
    if (index === 0 || index >= tailFrom || shown.has(message.id)) {
      if (run.length > 0) out.push({ kind: 'hidden', messages: run });
      run = [];
      out.push({ kind: 'message', message });
    } else {
      run.push(message);
    }
  });
  return out;
}

/**
 * What one click on a divider reveals: the `REVEAL_BATCH` NEWEST of its run,
 * the ones next to the message under it. Reading a conversation goes upward
 * from the recent context, as scrolling back through a chat does; the divider
 * stays where it was, above them, with its count going down.
 */
export function revealBatch(run: readonly ConversationMessage[]): ConversationMessage[] {
  return run.slice(-REVEAL_BATCH);
}

/**
 * A subject without its reply and forward prefixes ("Re:", "Fwd: RE[2]:",
 * "AW:", "SV:"…), whitespace collapsed and case folded: what two subjects are
 * compared by to tell whether a message says something the conversation's
 * subject does not.
 */
export function baseSubject(subject: string): string {
  return subject
    .replace(/^\s*((re|fwd?|aw|sv|vs|tr|wg)(\[\d+\])?\s*:\s*)+/i, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}
